import { useEffect, useRef, useState } from 'react'
import { loadStripe, type StripeElements } from '@stripe/stripe-js'
import { Loader, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { ORPCError } from '@orpc/client'
import { useOrpc } from '@/hooks/useClient'

// Stripe Elements card fields for the in-drawer VCC charge (accounting-plan Task 21).
// The card number / expiry / CVC live in Stripe iframes — card data never touches our
// code (no card state, only element refs and the returned paymentMethodId). The org's
// publishable key comes from GET /v2/billing/vcc/context/{encounterId} (Task 16); an
// org without Stripe configured surfaces the backend's 400 message as a terminal state.
export type VccCardApi = {
  /** stripe.createPaymentMethod over the mounted card element → PaymentMethod id. */
  createPaymentMethod: () => Promise<string>
}

type VccCardFieldsProps = {
  encounterId: string
  /** The charge hook once Elements are mounted; null while loading / after teardown. */
  onApiChange: (api: VccCardApi | null) => void
  /** True when all three card fields are complete (enables the Charge button). */
  onCompleteChange: (complete: boolean) => void
}

const FIELD_BOX_CLASS = 'rounded border border-gray-300 bg-white px-3 py-2'

function FieldLabel({ children }: { children: string }) {
  return (
    <Text component="label" size="sm" fw={500} c="dark">
      {children}
    </Text>
  )
}

export function VccCardFields({ encounterId, onApiChange, onCompleteChange }: VccCardFieldsProps) {
  const orpc = useOrpc()
  const numberRef = useRef<HTMLDivElement>(null)
  const expiryRef = useRef<HTMLDivElement>(null)
  const cvcRef = useRef<HTMLDivElement>(null)
  const [stripeError, setStripeError] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)

  // Callbacks live in refs so the Stripe effect never re-runs on parent re-renders —
  // remounting Elements would wipe whatever the biller already typed.
  const onApiChangeRef = useRef(onApiChange)
  onApiChangeRef.current = onApiChange
  const onCompleteChangeRef = useRef(onCompleteChange)
  onCompleteChangeRef.current = onCompleteChange

  const contextQuery = useQuery(orpc.billingV2VccContext.queryOptions({ input: { encounterId } }))
  const publishableKey = contextQuery.data?.publishableKey

  useEffect(() => {
    if (!publishableKey) return undefined
    let cancelled = false
    let elements: StripeElements | null = null

    const complete = { cardNumber: false, cardExpiry: false, cardCvc: false }
    const mount = async () => {
      const stripe = await loadStripe(publishableKey)
      if (cancelled) return
      if (!stripe) {
        setStripeError('Failed to load Stripe')
        return
      }
      if (!numberRef.current || !expiryRef.current || !cvcRef.current) return
      elements = stripe.elements()
      const reportComplete = () =>
        onCompleteChangeRef.current(complete.cardNumber && complete.cardExpiry && complete.cardCvc)
      const cardNumber = elements.create('cardNumber')
      cardNumber.mount(numberRef.current)
      cardNumber.on('change', (event) => {
        complete.cardNumber = event.complete
        reportComplete()
      })
      const cardExpiry = elements.create('cardExpiry')
      cardExpiry.mount(expiryRef.current)
      cardExpiry.on('change', (event) => {
        complete.cardExpiry = event.complete
        reportComplete()
      })
      const cardCvc = elements.create('cardCvc')
      cardCvc.mount(cvcRef.current)
      cardCvc.on('change', (event) => {
        complete.cardCvc = event.complete
        reportComplete()
      })
      setMounted(true)
      onApiChangeRef.current({
        createPaymentMethod: async () => {
          const result = await stripe.createPaymentMethod({ type: 'card', card: cardNumber })
          if (result.error) {
            throw new Error(result.error.message ?? 'Failed to validate the card')
          }
          return result.paymentMethod.id
        },
      })
    }
    void mount()

    return () => {
      cancelled = true
      setMounted(false)
      onApiChangeRef.current(null)
      onCompleteChangeRef.current(false)
      elements?.getElement('cardNumber')?.destroy()
      elements?.getElement('cardExpiry')?.destroy()
      elements?.getElement('cardCvc')?.destroy()
    }
  }, [publishableKey])

  if (contextQuery.isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader size="sm" />
      </div>
    )
  }

  if (contextQuery.error) {
    // The copay-style 400 ("Stripe is not configured for this organization") shown
    // verbatim; anything else gets a generic line.
    const message =
      contextQuery.error instanceof ORPCError && contextQuery.error.status === 400
        ? contextQuery.error.message
        : 'Failed to load the payment context'
    return (
      <Text size="sm" c="red">
        {message}
      </Text>
    )
  }

  if (stripeError) {
    return (
      <Text size="sm" c="red">
        {stripeError}
      </Text>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <FieldLabel>Card number</FieldLabel>
        <div ref={numberRef} className={FIELD_BOX_CLASS} data-testid="vcc-card-number" />
      </div>
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <FieldLabel>Expiry</FieldLabel>
          <div ref={expiryRef} className={FIELD_BOX_CLASS} data-testid="vcc-card-expiry" />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <FieldLabel>CVC</FieldLabel>
          <div ref={cvcRef} className={FIELD_BOX_CLASS} data-testid="vcc-card-cvc" />
        </div>
      </div>
      {!mounted ? (
        <Text size="xs" c="dimmed">
          Loading card fields…
        </Text>
      ) : null}
    </div>
  )
}
